/**
 * 知识点详情页 — 点击卡片进入
 *
 * 展示完整内容 + 来源 + 跳回原文
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { ArrowLeft, FileText, ExternalLink } from 'lucide-react';
import { Button, IconButton, Badge } from '@hump/ui';
import type { KnowledgeItem } from '@hump/shared-types';
import { knowledgeRepository } from '../lib/repository/knowledge';

const typeLabels = {
  word: '生词',
  snippet: '段落收藏',
  note: '批注',
} as const;

export function VocabDetailPage() {
  const { type: _type, itemId } = useParams<{ type: string; itemId: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<KnowledgeItem | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!itemId) return;
    knowledgeRepository.getAll().then((items) => {
      const found = items.find((i) => i.id === itemId);
      setItem(found ?? null);
      setLoading(false);
    });
  }, [itemId]);

  if (loading) return <div className="p-6"><p className="text-charcoal/50">加载中...</p></div>;
  if (!item) return <div className="p-6"><p className="text-error">知识点不存在</p></div>;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* 顶部导航 */}
      <div className="flex items-center gap-2 mb-6">
        <Link to="/hump">
          <IconButton size="sm" aria-label="返回"><ArrowLeft className="w-4 h-4" /></IconButton>
        </Link>
        <Badge variant="default">{typeLabels[item.type]}</Badge>
        <span className="text-xs text-charcoal/40 dark:text-charcoal-light/40 ml-auto">
          {new Date(item.createdAt).toLocaleString('zh-CN')}
        </span>
      </div>

      {/* 生词 */}
      {item.type === 'word' && (
        <div className="space-y-4">
          <div>
            <h1 className="text-3xl font-serif text-charcoal dark:text-charcoal-light">{item.word}</h1>
            <div className="flex items-center gap-3 mt-1">
              {item.phonetic && <span className="text-base text-charcoal/50 dark:text-charcoal-light/50">{item.phonetic}</span>}
              {item.pos && <Badge variant="info">{item.pos}</Badge>}
            </div>
          </div>
          {item.definitions && item.definitions.length > 0 && (
            <div>
              <h2 className="text-sm font-medium text-charcoal/60 dark:text-charcoal-light/60 mb-1">释义</h2>
              <ul className="space-y-1">
                {item.definitions.map((d, i) => (
                  <li key={i} className="text-charcoal dark:text-charcoal-light">{i + 1}. {d}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* 段落收藏 */}
      {item.type === 'snippet' && (
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-warm-white dark:bg-warm-dark border border-border-warm dark:border-border-warm-dark">
            <p className="text-charcoal dark:text-charcoal-light leading-relaxed">{item.text}</p>
          </div>
          {item.translation && (
            <div className="p-4 rounded-lg bg-oasis/5 border border-oasis/20">
              <p className="text-charcoal/70 dark:text-charcoal-light/70">{item.translation}</p>
            </div>
          )}
        </div>
      )}

      {/* 批注 */}
      {item.type === 'note' && (
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-camel/5 border border-camel/20">
            <p className="text-charcoal dark:text-charcoal-light whitespace-pre-wrap">{item.note}</p>
          </div>
          {item.context && (
            <div>
              <h2 className="text-sm font-medium text-charcoal/60 dark:text-charcoal-light/60 mb-1">关联原文</h2>
              <p className="text-sm text-charcoal/50 dark:text-charcoal-light/50 italic p-3 rounded-md bg-warm-white dark:bg-warm-dark">{item.context}</p>
            </div>
          )}
        </div>
      )}

      {/* 来源 */}
      {item.docId && (
        <div className="mt-6 pt-4 border-t border-border-warm dark:border-border-warm-dark">
          <h2 className="text-sm font-medium text-charcoal/60 dark:text-charcoal-light/60 mb-2">来源</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate(`/reader/${item.docId}`)}>
            <FileText className="w-4 h-4 mr-1" />
            {item.docTitle ?? '查看原文'}
            <ExternalLink className="w-3 h-3 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
